/* Ash Mega Menu — hover-open with grace, single-active across page,
 * click still works for touch / keyboard. */
class AshMegaMenu extends HTMLElement {
  connectedCallback() {
    this._trigger = this.querySelector('[data-ash-mm-trigger]');
    this._panel = this.querySelector('[data-ash-mm-panel]');
    this._rail = this.querySelector('[data-ash-mm-rail]');
    /* Plain nav items are NOT wrapped in <ash-mega-menu> anymore.
       Still guard: without a real dropdown panel, skip all hover/open logic. */
    if (!this._trigger || !this._panel) return;
    this.classList.add('ash-mm--has-dropdown');

    /* For the block-based architecture: each rendered _ash-mega-tab
     * pane carries its label/URL/id as data attributes. The rail is
     * empty in liquid; we build the rail buttons from those panes here
     * so the user only manages tabs as blocks, not as duplicate rail
     * entries. Safe no-op for legacy panels that ship their own rail. */
    if (this._rail && this._rail.children.length === 0) {
      var panes = this._panel.querySelectorAll('[data-ash-mn-tab-id]');
      var i = 0;
      var firstId = null;
      panes.forEach(function (pane) {
        var tid = pane.getAttribute('data-ash-mn-tab-id');
        var tlabel = pane.getAttribute('data-ash-mn-tab-label') || ('Tab ' + (++i));
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ash-mn__rail-btn button-unstyled';
        btn.setAttribute('data-ash-mm-tab', tid);
        btn.setAttribute('role', 'tab');
        btn.textContent = tlabel;
        if (firstId === null) {
          firstId = tid;
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          pane.classList.add('is-active');
          pane.removeAttribute('hidden');
        } else {
          btn.setAttribute('aria-selected', 'false');
          pane.setAttribute('hidden', '');
        }
        var li = document.createElement('li');
        li.className = 'ash-mn__rail-item';
        li.appendChild(btn);
        this._rail.appendChild(li);
        pane.setAttribute('data-ash-mm-pane', tid);
      }, this);
    }

    var self = this;
    this._closeTimer = null;

    /* Hover open with grace period so moving cursor trigger → panel
       doesn't close the panel. */
    this.addEventListener('mouseenter', function () {
      clearTimeout(self._closeTimer);
      self.open();
    });
    this.addEventListener('mouseleave', function () {
      clearTimeout(self._closeTimer);
      self._closeTimer = setTimeout(function () { self.close(); }, 160);
    });

    /* Trigger click: if it's an <a href> with a real URL, let the
       browser navigate to that collection/page. If it's a button OR
       a placeholder link (href === '#'), preventDefault and toggle
       the panel instead — useful for keyboard / touch. Touch devices
       still get explicit toggle on first tap via the panel-toggle
       button; clicking the label itself navigates. */
    this._trigger.addEventListener('click', function (e) {
      var href = self._trigger.getAttribute('href');
      var isNavLink = self._trigger.tagName === 'A' && href && href !== '#';
      if (isNavLink) {
        return; // let default navigation happen
      }
      e.preventDefault();
      e.stopPropagation();
      self.toggle();
    });

    /* Tab switching — click + hover */
    if (this._rail) {
      this._rail.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ash-mm-tab]');
        if (!btn) return;
        /* L2 collection links: <a href> navigates; buttons still switch tabs only. */
        if (btn.tagName === 'A' && btn.getAttribute('href') && btn.getAttribute('href') !== '#') {
          return;
        }
        e.preventDefault();
        self.activate(btn.getAttribute('data-ash-mm-tab'));
      });
      this._rail.addEventListener('mouseover', function (e) {
        var btn = e.target.closest('[data-ash-mm-tab]');
        if (!btn) return;
        self.activate(btn.getAttribute('data-ash-mm-tab'));
      });
    }

    /* Sub-rail (L3) switching inside an L2 pane — click + hover */
    this._panel.addEventListener('click', function (e) {
      var sbtn = e.target.closest('[data-ash-mm-subtab]');
      if (!sbtn) return;
      e.preventDefault();
      self.activateSub(sbtn);
    });
    this._panel.addEventListener('mouseover', function (e) {
      var sbtn = e.target.closest('[data-ash-mm-subtab]');
      if (!sbtn) return;
      self.activateSub(sbtn);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });
    document.addEventListener('click', function (e) {
      if (!self.isOpen()) return;
      if (self.contains(e.target)) return;
      self.close();
    });
    document.addEventListener('ash-mm:open', function (e) {
      if (e.detail && e.detail.source !== self) self.close();
    });

    /* Close on any meaningful page scroll. The check ignores the tiny
       scroll deltas the layout itself fires when the panel mounts. */
    var lastY = window.scrollY;
    window.addEventListener('scroll', function () {
      if (!self.isOpen()) { lastY = window.scrollY; return; }
      if (Math.abs(window.scrollY - lastY) > 4) self.close();
      lastY = window.scrollY;
    }, { passive: true });
  }

  isOpen() { return this.hasAttribute('data-open'); }
  toggle() { this.isOpen() ? this.close() : this.open(); }

  open() {
    if (this.isOpen()) return;
    this.setAttribute('data-open', '');
    this._trigger.setAttribute('aria-expanded', 'true');
    document.dispatchEvent(new CustomEvent('ash-mm:open', { detail: { source: this } }));

    var active = this.querySelector('[data-ash-mm-tab].is-active');
    if (!active) {
      var first = this.querySelector('[data-ash-mm-tab]');
      if (first) this.activate(first.getAttribute('data-ash-mm-tab'));
    }
  }
  close() {
    this.removeAttribute('data-open');
    this._trigger.setAttribute('aria-expanded', 'false');
  }
  activate(id) {
    if (!id) return;
    this.querySelectorAll('[data-ash-mm-tab]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-ash-mm-tab') === id);
      b.setAttribute('aria-selected', b.getAttribute('data-ash-mm-tab') === id ? 'true' : 'false');
    });
    this.querySelectorAll('[data-ash-mm-pane]').forEach(function (p) {
      var match = p.getAttribute('data-ash-mm-pane') === id;
      p.classList.toggle('is-active', match);
      if (match) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    /* Mobile cards position auto-resolve: when the trigger setting
       is "auto", a pane's card count decides whether cards sit
       above (3+ cards) or below (≤2 cards) the rail. */
    if (this._panel && this._panel.getAttribute('data-ash-mn-mobile-cards') === 'auto') {
      var active = this.querySelector('[data-ash-mm-pane].is-active');
      var count = active ? active.querySelectorAll('.ash-mn__card').length : 0;
      this._panel.setAttribute('data-mobile-cards-resolved', count >= 3 ? 'top' : 'bottom');
    }
  }
  activateSub(btn) {
    var id = btn.getAttribute('data-ash-mm-subtab');
    if (!id) return;
    var parentPane = btn.closest('[data-ash-mm-pane]');
    var scope = parentPane || this;
    scope.querySelectorAll('[data-ash-mm-subtab]').forEach(function (b) {
      if (b.closest('[data-ash-mm-pane]') !== parentPane) return;
      var match = b.getAttribute('data-ash-mm-subtab') === id;
      b.classList.toggle('is-active', match);
      b.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    scope.querySelectorAll('[data-ash-mm-subpane]').forEach(function (p) {
      if (p.closest('[data-ash-mm-pane]') !== parentPane) return;
      var match = p.getAttribute('data-ash-mm-subpane') === id;
      p.classList.toggle('is-active', match);
      if (match) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }
}

if (!customElements.get('ash-mega-menu')) {
  customElements.define('ash-mega-menu', AshMegaMenu);
}

/* ============================================================
   Auto-fit script.
   If `.ash-mm-host__nav` (the row of mega-menu triggers) is wider
   than its grid column — i.e. visually overlaps the right-side
   header icons (search / account / cart) — shrink the trigger
   font-size step by step until it fits. The merchant-configured
   font-size stays in force when nothing overflows.

   Implementation:
     - Walk every `.ash-mm-host` host in the document.
     - On each measurement, compare nav's scrollWidth to its
       clientWidth + a small safety margin so we leave a visible
       gap between the last trigger label and the icons column.
     - If overflowing, set an inline `--ash-mn-fs-auto` CSS
       variable on the host AND inline `font-size` on each
       trigger label (so it beats the scoped !important rule).
     - On resize / orientation change, recompute. If the override
       is no longer needed (window grew), restore the
       merchant-configured value by clearing the inline overrides.
   ============================================================ */
(function () {
  if (window.__ashMmAutofitInit) return;
  window.__ashMmAutofitInit = true;

  var SAFETY_MARGIN = 24;     // px between last label and icons column
  var MIN_FONT = 7;           // floor — never shrink below 7px

  function getLabelEls(host) {
    return host.querySelectorAll('.ash-mn__trigger-label');
  }

  function getRowContainer(host) {
    return host.closest('.header__column, .header__columns, .header__row, header-component') || host.parentElement;
  }

  /* Find the right-side icons cluster so we can measure its actual
     left edge in viewport coordinates. The header lays the menu and
     icons in independent CSS-grid columns, so nav.scrollWidth never
     reports an "overflow" even when the nav visually slides under
     the icons. We have to compare bounding rects instead. */
  function findIconsRect(host) {
    // Look in the same header for header-actions or the right column.
    var header = host.closest('header-component, #header-component') || document.querySelector('header-component, #header-component');
    if (!header) header = document;
    var icons = header.querySelector('header-actions, .header-actions, .header__column--right');
    if (!icons) return null;
    return icons.getBoundingClientRect();
  }

  function getLastLabel(host) {
    var labels = getLabelEls(host);
    return labels.length ? labels[labels.length - 1] : null;
  }

  function getConfiguredFontPx(host, isMobile) {
    // Read the scoped style's literal value off the nav element by
    // sampling a trigger label's computed font-size BEFORE any
    // overrides — cache it on the host so repeated measurements
    // don't compound with our own inline overrides.
    var cacheKey = isMobile ? '__ashMmCfgM' : '__ashMmCfgD';
    if (host[cacheKey]) return host[cacheKey];
    var first = host.querySelector('.ash-mn__trigger-label');
    if (!first) return 12;
    // Temporarily strip any prior override to get the true cfg value.
    var prev = first.style.fontSize;
    first.style.fontSize = '';
    var cs = window.getComputedStyle(first).fontSize;
    first.style.fontSize = prev;
    var px = parseFloat(cs) || 12;
    host[cacheKey] = px;
    return px;
  }

  function applyFontPx(host, px) {
    host.style.setProperty('--ash-mn-fs-auto', px + 'px');
    var labels = getLabelEls(host);
    for (var i = 0; i < labels.length; i++) {
      labels[i].style.fontSize = px + 'px';
    }
  }

  function clearOverride(host) {
    host.style.removeProperty('--ash-mn-fs-auto');
    var labels = getLabelEls(host);
    for (var i = 0; i < labels.length; i++) {
      labels[i].style.fontSize = '';
    }
  }

  /* True overlap check: returns the px the nav extends PAST the icons'
     left edge. Positive = overlap. */
  function overflowAmount(host) {
    var iconsRect = findIconsRect(host);
    if (!iconsRect) return 0;
    var lastLabel = getLastLabel(host);
    if (!lastLabel) return 0;
    var lastRect = lastLabel.getBoundingClientRect();
    return (lastRect.right + SAFETY_MARGIN) - iconsRect.left;
  }

  function autofitOne(host) {
    var nav = host.querySelector('.ash-mm-host__nav');
    if (!nav) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var configured = getConfiguredFontPx(host, isMobile);

    /* Restore configured size before measuring so we don't compound
       our previous shrink. */
    clearOverride(host);
    // Force layout flush.
    // eslint-disable-next-line no-unused-vars
    var _ = nav.offsetWidth;

    if (overflowAmount(host) <= 0) return; // fits at configured size

    /* Binary-shrink from configured → MIN_FONT until last label's
       right edge stays SAFETY_MARGIN clear of the icons' left edge. */
    var hi = configured;
    var lo = MIN_FONT;
    var best = MIN_FONT;
    for (var i = 0; i < 14 && hi - lo > 0.05; i++) {
      var mid = (hi + lo) / 2;
      applyFontPx(host, mid);
      // eslint-disable-next-line no-unused-vars
      var _f = nav.offsetWidth;
      if (overflowAmount(host) <= 0) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
    applyFontPx(host, Math.floor(best * 10) / 10);
  }

  function autofitAll() {
    var hosts = document.querySelectorAll('.ash-mm-host');
    for (var i = 0; i < hosts.length; i++) autofitOne(hosts[i]);
  }

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      autofitAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule);
  // Fonts can shift widths as they load — recompute when they finish.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(schedule);
  }
  // Catch DOM mutations (sticky header swaps, async section loads).
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes && muts[i].addedNodes.length) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType === 1 && (
            n.matches && n.matches('.ash-mm-host') ||
            n.querySelector && n.querySelector('.ash-mm-host')
          )) {
            schedule();
            return;
          }
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
